import { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text, TextButton } from '@modusign/design-system';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import LoadingSpinner from '@app/components/LoadingSpinner';
import { ExportInputValuesStatus, getExportValuesState } from '@app/features/exportInputValues';

interface MinimizedExportInputValuesModalProps {
  status: ExportInputValuesStatus;
  progressPercent: number;
  failedDocumentCount: number;
  onMaximize: () => void;
}
export default function ExportInputValuesProgressModalSnackBar({
  onMaximize,
  progressPercent,
  status,
  failedDocumentCount,
}: MinimizedExportInputValuesModalProps) {
  const { isModalMinimized } = useSelector(getExportValuesState);

  const statusDescription = useMemo(() => {
    let description = '';

    switch (status) {
      case 'IN_PROGRESS':
        description = `입력값 추출을 진행하고 있습니다. (${progressPercent}%)`;

        if (progressPercent <= 10) {
          description = '데이터 추출을 준비하고 있습니다.';
        }

        if (progressPercent >= 70) {
          description = 'CSV 파일을 생성하고 있습니다.';
        }
        return description;

      case 'COMPLETED':
        if (failedDocumentCount > 0) {
          description = `입력값 추출이 완료되었습니다. (실패 ${failedDocumentCount}건)`;
        } else {
          description = `입력값 추출이 완료되었습니다.`;
        }
        return description;

      case 'FAILED':
        if (failedDocumentCount > 0) {
          description = `입력값 추출이 완료되었습니다. (실패 ${failedDocumentCount}건)`;
        }
        return description;
      default:
        return '';
    }
  }, [failedDocumentCount, progressPercent, status]);

  if (!isModalMinimized) {
    return null;
  }

  return (
    <Box maxWidth="xs" p={4} width="min(450px, 100vw)">
      <Flex
        alignItems="center"
        bg="notificationBackgroundSecondaryUltraDark"
        borderRadius="4px"
        boxShadow="0px 4px 10px rgba(0, 0, 0, 0.25)"
        justifyContent="space-between"
        pl={5}
        pr={4}
        py={5}
      >
        <Flex alignItems="center">
          <StatusIcon
            status={status === 'COMPLETED' && failedDocumentCount > 0 ? 'FAILED' : status}
          />
          <Text color="textNeutralLightest" fontWeight="medium" ml={4}>
            {statusDescription}
          </Text>
        </Flex>
        <TextButton mode="night" onClick={onMaximize}>
          자세히
        </TextButton>
      </Flex>
    </Box>
  );
}

function StatusIcon({ status }: { status: ExportInputValuesStatus }) {
  switch (status) {
    case 'IN_PROGRESS':
      return (
        <Box color="iconNeutralLightest" fontSize="18px">
          <LoadingSpinner color="white" size={18} />
        </Box>
      );
    case 'COMPLETED':
      return (
        <Box color="iconPositiveBase" fontSize="18px">
          <FontAwesomeIcon icon={faCheckCircle as IconLookup} />
        </Box>
      );
    case 'FAILED':
      return (
        <Box color="iconNegativeBase" fontSize="18px">
          <FontAwesomeIcon icon={faTriangleExclamation as IconLookup} />
        </Box>
      );
    default:
      return null;
  }
}
